import { NOT_FOUND, redirect, scrollBehavior } from 'redux-first-router';

import { isAppPayload, getPayloadType } from 'utils';
import { isAndroid, isiOS, isWindows, pageLayout } from 'config';
import {
  fetchFeaturesAndUser,
  fetchHasGalleryGrant,
  fetchApp,
  fetchCollection,
  fetchPageLayout,
  searchAllApps,
  fetchInstalledApps
} from 'actions/registry';
import {
  fetchDeviceInfo,
  syncInstalledApps,
  fetchHasSettings,
  fetchOsPermission
} from 'actions/gallery';
import { PageRoutes } from './constants';

// https://jira.fitbit.com/browse/IPD-76475
const isOutdated = (phoneAppVersion, { showHiggsCU2, enableGalleryGating }) => {
  // If not enabled always return false to ignore this check
  if (!enableGalleryGating) {
    return false;
  }
  if (!showHiggsCU2) {
    return false;
  }
  if (!phoneAppVersion) {
    return true;
  }
  const [major, minor] = phoneAppVersion.split('.');
  const majorVersion = parseInt(major, 10);
  const minorVersion = parseInt(minor, 10);

  if (majorVersion < 2) {
    return true; // out of date
  } else if (majorVersion > 2) {
    return false; // newer major version than needed
  } else if (isiOS && minorVersion < 43) {
    // iOS requires >= v2.43
    return true;
  } else if (isWindows && minorVersion < 28) {
    // Windows requires >= 2.28
    return true;
  } else if (isAndroid && minorVersion < 62) {
    // Android requires >= v2.62
    return true;
  }
  return false;
};

const ensureSetup = (thunk) => async (dispatch, getState) => {
  const redirectOrContinue = async () => {
    const {
      deviceInfo: { phoneAppVersion }
    } = getState().gallery;
    const {
      features: {
        flags: { showHiggsCU2, enableGalleryGating }
      }
    } = getState().registry;

    if (isOutdated(phoneAppVersion, { showHiggsCU2, enableGalleryGating })) {
      await dispatch(redirect({ type: 'OUTDATED' }));
      return;
    }

    await thunk(dispatch, getState);

    // Trigger scroll update after thunks run
    const { prev, ...next } = getState().location;
    scrollBehavior().updateScroll(prev, next);
  };

  const {
    deviceInfo: { wireId }
  } = getState().gallery;
  const {
    features: { complete: featuresLoaded }
  } = getState().registry;

  if (wireId && featuresLoaded) {
    await redirectOrContinue();
    return;
  }

  // Routes run potentially before this is ready so
  // need to wait here before making any calls to mobile
  window.__webview_comms__.on('ready', async () => {
    if (!wireId) {
      await dispatch(fetchDeviceInfo());
    }
    if (!featuresLoaded) {
      await dispatch(fetchFeaturesAndUser());
    }

    await redirectOrContinue();
  });
};

const forceLowerCasePath = {
  fromPath(pathSegment) {
    return pathSegment && pathSegment.toLowerCase();
  }
};

const searchPage = {
  path: '/v2/search/:appType(apps|clocks|tiles)/:text',
  thunk: ensureSetup((dispatch, getState) => {
    const {
      location: { payload },
      registry: {
        features: { flags }
      }
    } = getState();

    if (!flags.showSearch) {
      dispatch(redirect({ type: NOT_FOUND }));
      return;
    }
  })
};

const galleryPage = {
  thunk: ensureSetup((dispatch, getState) => {
    const {
      location: { payload, query: { layoutId } = {} },
      registry: {
        pageLayoutItems,
        galleryGrant: { complete: fetchedHasGalleryGrant }
      }
    } = getState();

    const layoutType = getPayloadType(payload);

    return Promise.all([
      dispatch(syncInstalledApps()),
      pageLayoutItems[layoutType]
        ? undefined
        : dispatch(
            fetchPageLayout(layoutType, {
              id: layoutId,
              limit: pageLayout[layoutType].numPreviewApps
            })
          ),
      fetchedHasGalleryGrant ? undefined : dispatch(fetchHasGalleryGrant())
    ]);
  })
};

const permissionsPage = {
  thunk: ensureSetup(async (dispatch, getState) => {
    const payload = getState().location.payload;
    const { id } = payload;
    if (!id) {
      dispatch(redirect({ type: NOT_FOUND }));
      return;
    }

    const installedApps = getState().registry.installedApps;
    const isApp = isAppPayload(payload);

    await dispatch(fetchApp(id));

    const apps = getState().registry.apps;
    if (!apps || !apps.hasOwnProperty(id)) {
      dispatch(redirect({ type: NOT_FOUND }));
      return;
    }

    if (!installedApps[id]) {
      dispatch(
        redirect({
          type: isApp ? 'APP' : 'CLOCK',
          payload: { id }
        })
      );
    }

    await Promise.all([
      dispatch(fetchOsPermission('location')),
      dispatch(fetchOsPermission('calendar')),
      dispatch(fetchOsPermission('notifications'))
    ]);
  })
};

const galleryClocksPath = '/v2/:appType(clocks)/gallery';
const galleryAppsPath = '/v2/:appType(apps)/gallery';
const galleryTilesPath = '/v2/:appType(tiles)/gallery';
const discoverAppsPath = '/v2/:appType(apps)/discover';
const discoverClocksPath = '/v2/:appType(clocks)/discover';
const discoverTilesPath = '/v2/:appType(tiles)/discover';

const routesMap = {
  [PageRoutes.HOME]: {
    path: '/',
    thunk: (dispatch) =>
      dispatch(
        redirect({
          type: PageRoutes.GALLERY_V2_CLOCKS,
          payload: { appType: 'clocks' }
        })
      )
  },
  [PageRoutes.MY_APPS]: {
    path: '/:appType(apps|clocks)',
    thunk: ensureSetup((dispatch, getState) => {
      const isApp = isAppPayload(getState().location.payload);

      return Promise.all([
        dispatch(fetchOsPermission('location')),
        dispatch(fetchOsPermission('calendar')),
        dispatch(fetchOsPermission('notifications')),
        dispatch(syncInstalledApps({ getAppData: true }))
      ]);
    })
  },
  [PageRoutes.GALLERY]: {
    path: '/:appType(apps|clocks|tiles)/gallery',
    thunk: ensureSetup((dispatch, getState) => {
      const {
        location: { payload, query: { layoutId } = {} },
        registry: { pageLayoutItems }
      } = getState();
      const payloadType = getPayloadType(payload);

      return Promise.all([
        dispatch(syncInstalledApps()),
        pageLayoutItems[payloadType]
          ? undefined
          : dispatch(
              fetchPageLayout(payloadType, {
                id: layoutId,
                limit: pageLayout[payloadType].numPreviewApps
              })
            )
      ]);
    })
  },
  [PageRoutes.SEARCH]: {
    path: '/:appType(apps|clocks)/search',
    thunk: ensureSetup((dispatch, getState) => {
      const {
        location: { payload, query: { text = '' } = {} },
        registry: {
          features: { flags }
        }
      } = getState();
      if (!flags.showSearch) {
        dispatch(redirect({ type: NOT_FOUND }));
        return;
      }
      const isApp = isAppPayload(payload);

      const searchType = isApp ? 'APP' : 'CLOCK';
      dispatch(searchAllApps(searchType, text));
    })
  },
  [PageRoutes.COLLECTION]: {
    ...forceLowerCasePath,
    path: '/:appType(apps|clocks)/:collectionType(collection|category)/:id',
    thunk: ensureSetup((dispatch, getState) => {
      const {
        location: {
          payload,
          payload: { collectionType, id }
        },
        registry: { collections }
      } = getState();
      const isApp = isAppPayload(payload);
      return Promise.all([
        dispatch(syncInstalledApps()),
        id in collections
          ? undefined
          : dispatch(fetchCollection(id, collectionType))
      ]);
    })
  },
  [PageRoutes.APP]: {
    ...forceLowerCasePath,
    path: '/:appType(app|clock|tile)/:id',
    thunk: ensureSetup(async (dispatch, getState) => {
      const payload = getState().location.payload;
      const { id } = payload;
      if (!id) {
        dispatch(redirect({ type: NOT_FOUND }));
        return;
      }

      await Promise.all([
        dispatch(fetchOsPermission('location')),
        dispatch(fetchOsPermission('calendar')),
        dispatch(fetchOsPermission('notifications'))
      ]);

      await dispatch(fetchApp(id));

      const apps = getState().registry.apps;
      if (!apps || !apps.hasOwnProperty(id)) {
        dispatch(redirect({ type: NOT_FOUND }));
        return;
      }

      const isApp = isAppPayload(payload);

      await dispatch(fetchHasSettings(id));
    })
  },
  [PageRoutes.PERMISSION]: {
    ...forceLowerCasePath,
    path: '/(app|clock)/:id/permissions',
    ...permissionsPage
  },
  [PageRoutes.PERMISSION_V2]: {
    ...forceLowerCasePath,
    path: '/v2/(app|clock)/:id/permissions',
    ...permissionsPage
  },
  [PageRoutes.GALLERY_V2_CLOCKS]: {
    path: galleryClocksPath,
    thunk: ensureSetup(async (dispatch, getState) => {
      await dispatch(syncInstalledApps({ getAppData: true }));

      const {
        registry: {
          galleryGrant: { complete: fetchedHasGalleryGrant }
        }
      } = getState();

      return Promise.all([
        dispatch(fetchOsPermission('location')),
        dispatch(fetchOsPermission('calendar')),
        dispatch(fetchOsPermission('notifications')),
        fetchedHasGalleryGrant ? undefined : dispatch(fetchHasGalleryGrant())
      ]);
    })
  },
  [PageRoutes.GALLERY_V2_APPS]: {
    path: galleryAppsPath,
    thunk: ensureSetup(async (dispatch, getState) => {
      await dispatch(syncInstalledApps({ getAppData: true }));

      const {
        registry: {
          galleryGrant: { complete: fetchedHasGalleryGrant }
        }
      } = getState();

      return Promise.all([
        dispatch(fetchOsPermission('location')),
        dispatch(fetchOsPermission('calendar')),
        dispatch(fetchOsPermission('notifications')),
        fetchedHasGalleryGrant ? undefined : dispatch(fetchHasGalleryGrant())
      ]);
    })
  },
  [PageRoutes.DISCOVER_V2_APPS]: {
    path: discoverAppsPath,
    ...galleryPage
  },
  [PageRoutes.DISCOVER_V2_CLOCKS]: {
    path: discoverClocksPath,
    ...galleryPage
  },
  [PageRoutes.GALLERY_V2_TILES]: {
    path: galleryTilesPath,
    thunk: ensureSetup(async (dispatch, getState) => {
      await dispatch(syncInstalledApps({ getAppData: true }));

      const {
        registry: {
          galleryGrant: { complete: fetchedHasGalleryGrant }
        }
      } = getState();

      return Promise.all([
        dispatch(fetchOsPermission('location')),
        dispatch(fetchOsPermission('calendar')),
        dispatch(fetchOsPermission('notifications')),
        fetchedHasGalleryGrant ? undefined : dispatch(fetchHasGalleryGrant())
      ]);
    })
  },
  [PageRoutes.DISCOVER_V2_TILES]: {
    path: discoverTilesPath,
    ...galleryPage
  },
  [PageRoutes.MINE_V2]: {
    path: '/v2/device',
    thunk: (dispatch) =>
      dispatch(
        redirect({
          type: PageRoutes.GALLERY_V2_CLOCKS,
          payload: { appType: 'clocks' }
        })
      )
  },
  [PageRoutes.LEGACY_DEVICE]: {
    path: '/v2/legacy-device',
    thunk: ensureSetup(async (dispatch, getState) => {
      await dispatch(syncInstalledApps({ getAppData: true }));

      const {
        registry: {
          galleryGrant: { complete: fetchedHasGalleryGrant }
        }
      } = getState();

      return Promise.all([
        dispatch(fetchOsPermission('location')),
        dispatch(fetchOsPermission('calendar')),
        dispatch(fetchOsPermission('notifications')),
        fetchedHasGalleryGrant ? undefined : dispatch(fetchHasGalleryGrant())
      ]);
    })
  },
  [PageRoutes.MY_TILES_V2]: {
    path: '/v2/my_tiles',
    thunk: ensureSetup(async (dispatch) => {
      await dispatch(syncInstalledApps({ getAppData: true }));
    })
  },
  [PageRoutes.COLLECTION_V2]: {
    ...forceLowerCasePath,
    path:
      '/v2/:appType(apps|clocks|tiles)/:collectionType(collection|category)/:id',
    thunk: ensureSetup((dispatch, getState) => {
      const {
        location: {
          payload,
          payload: { collectionType, id }
        },
        registry: {
          collections,
          galleryGrant: { complete: fetchedHasGalleryGrant }
        }
      } = getState();
      const isApp = isAppPayload(payload);

      return Promise.all([
        dispatch(syncInstalledApps()),
        id in collections
          ? undefined
          : dispatch(fetchCollection(id, collectionType)),
        fetchedHasGalleryGrant ? undefined : dispatch(fetchHasGalleryGrant())
      ]);
    })
  },
  [PageRoutes.APP_V2]: {
    ...forceLowerCasePath,
    path: '/v2/:appType(app|clock|tile)/:id',
    thunk: ensureSetup(async (dispatch, getState) => {
      const {
        location: {
          payload,
          payload: { id, appType }
        },
        registry: {
          galleryGrant: { complete: fetchedHasGalleryGrant }
        }
      } = getState();

      if (!id) {
        dispatch(redirect({ type: NOT_FOUND }));
        return;
      }

      await Promise.all([
        dispatch(fetchOsPermission('location')),
        dispatch(fetchOsPermission('calendar')),
        dispatch(fetchOsPermission('notifications'))
      ]);

      let apps = getState().registry.apps;

      // the app was opened using a deeplink and the installed apps were not retrieved from backend
      // otherwise, an error will be displayed after install
      if (!apps || Object.keys(apps).length === 0) {
        await dispatch(fetchInstalledApps());
      }

      await dispatch(fetchApp(id));

      if (appType === 'tile') {
        await dispatch(syncInstalledApps({ getAppData: true }));
      }

      apps = getState().registry.apps;

      if (!apps || !apps.hasOwnProperty(id)) {
        dispatch(redirect({ type: NOT_FOUND }));
        return;
      }

      if (!fetchedHasGalleryGrant) {
        await dispatch(fetchHasGalleryGrant());
      }

      await dispatch(fetchHasSettings(id));
    })
  },
  [PageRoutes.SEARCH_V2]: {
    ...searchPage,
    path: '/v2/search'
  },
  [PageRoutes.SEARCH_ITEMS_V2]: searchPage,
  OUTDATED: {
    path: '/outdated'
  },
  ERROR: {
    path: '/error'
  }
};

export default routesMap;
